import Link from 'next/link';
import { useEffect, useContext, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppBar, Toolbar, Typography, Grid, MenuItem, Divider, IconButton, Popper, Grow, Paper, ClickAwayListener, MenuList } from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircleSharp';
import MenuIcon from '@material-ui/icons/MenuSharp';
import { makeStyles } from '@material-ui/core/styles';

import { doLogout } from "../utils/authLib";
import theme, { gradient } from '../utils/theme';
import { AppContext } from './AppState';
import QaToolbar from './QaToolbar';
import MenuDropdown from './MenuDropdown';
import ConfirmModal from './ConfirmModal';
import NavigateModal from './NavigateModal';
import TimedAlert from './TimedAlert';
import HelpButton from './HelpButton';
import ServiceCreditsModal from './ServiceCreditsModal';
import { useAuth } from '../utils/useAuth';
import { useUser } from '../utils/users';
import { CreditType } from '../utils/shared/business';


const useStyles = makeStyles((theme) => ({
  // Keep total header height in sync with the MaterialViewer.js useEffect that calls setCssWidth()
  navbar: {
    marginBottom: '25px',
    background: theme.palette.accent.main,
    //boxShadow: '0px 3px 5px #bbb',
  },
  grow: {
    flexGrow: 1,
  },
  navLink: {
    color: '#ffffff',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  menuText: {
    lineHeight: '50px',
    color: '#303031',
    marginRight: '20px',
    textWrap: 'nowrap',
  },
  emailText: {
    color: '#777',
    fontSize: '0.7em',
  },
  menuSecondary: {
    fontSize: '1em',
  },
  menuShort: {
    lineHeight: '25px !important',
  },
  serviceText: {
    color: theme.palette.secondary.light,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  menuCreditsText: {
    fontSize: '0.7em',
    color: '#777',
    marginTop: theme.spacing(0.5),
    float: 'right',
  },
  stageLabel: {
    backgroundColor: theme.palette.secondary.light,
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '1em',
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
    display: 'inline-flex',
    alignItems: 'center',
  },
  sectionFlex: {
    display: 'flex',
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  welcomeText: {
    marginBottom: theme.spacing(1),
  },
}));

export default function Navbar(props) {
  const { hidden } = props;
  const { loginWithRedirect, logout, } = useAuth0();
  const { isAuthenticated, user } = useAuth();

  const { userItem } = useUser();

  const classes = useStyles();
  const {
    credits,
    serviceCredits,
    galleryUser,
    qaDisplay,
    alert,
    redirect, setRedirect,
  } = useContext(AppContext);

  const { userItem: galleryUserItem } = useUser(galleryUser);

  const [showWelcomeModal, setShowWelcomeModal] = useState(null);
  const [showNavModal, setShowNavModal] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showServiceCreditsModal, setShowServiceCreditsModal] = useState(false);
  const [defaultCreditType, setDefaultCreditType] = useState(CreditType.USER_CREDIT);

  const menuAnchorRef = useRef(null);
  const accountAnchorRef = useRef(null);

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const handleAccountMenuClose = () => {
    setAccountMenuOpen(false);
  };

  const handleAccountMenuToggle = () => {
    setAccountMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMenuItemClick = (action) => {
    action();
    setAccountMenuOpen(false);
  };

  useEffect(() => {
    async function onLoad() {
      if (userItem) {
        const createdAt = new Date(userItem.creationDate);
        const timeNow = new Date();
        const ageSeconds = (timeNow.getTime() - createdAt.getTime()) / 1000;
        if (ageSeconds < 30 && showWelcomeModal !== false) {
          var message = <>
            <Typography className={classes.welcomeText}>
              You&apos;re moments away from creating your own beautiful digital fabrics.
            </Typography>
          </>;
          if (userItem.credits > 0) {
            message = <>
              {message}
              <Typography className={classes.welcomeText}>
                To thank you for trying us out, we&apos;ve loaded your account with <b>{userItem.credits} free credit{userItem.credits > 1 && 's'}.</b>
              </Typography>
            </>;
          }

          setWelcomeMessage(message);
          setShowWelcomeModal(true);
        }
      }
    }
    onLoad();
  }, [classes, userItem, setShowWelcomeModal, setWelcomeMessage, showWelcomeModal]);

  async function handleCloseWelcome() {
    setShowWelcomeModal(false);
  }

  const isSelfUser = user && (user.sub == galleryUser);

  return (<>
    {!hidden && (
      <AppBar position="static" className={classes.navbar}>
        <Toolbar>
          <div className={classes.sectionMobile}>
            <MenuDropdown
              anchorRef={menuAnchorRef}
              icon={<MenuIcon />}
            >
              {isAuthenticated && (
                <>
                  <Link href="/home"><MenuItem>Home</MenuItem></Link>
                </>
              )}
            </MenuDropdown>
          </div>
          <div className={classes.sectionFlex}>
              <img src="BDT_Lockup_Sec_BLU.png" style={{height: '32px', cursor: 'pointer'}} onClick={() => setRedirect('/home')} />
              {process.env.NEXT_PUBLIC_STAGE_APP !== 'prod' && (
                <span className={classes.stageLabel}>{process.env.NEXT_PUBLIC_STAGE_API}</span>
              )}
          </div>

          <div className={classes.grow} />

          <div className={classes.sectionFlex}>
            <QaToolbar />
          </div>

          {isAuthenticated && (
            <div className={classes.sectionDesktop}>
              <div>
                <Typography
                  className={`${classes.menuText} ${classes.menuSecondary} ${((isSelfUser || (userItem?.staff && qaDisplay)) && classes.navLink)} ${qaDisplay && classes.menuShort}`}
                  onClick={() => {
                    if (userItem?.staff && qaDisplay) {
                      setDefaultCreditType(CreditType.USER_CREDIT);
                      setShowServiceCreditsModal(true);
                    } else if (isSelfUser) {
                      setRedirect('/buy?p=credits');
                    }
                  }}
                  style={{ cursor: (isSelfUser || (userItem?.staff && qaDisplay)) ? 'pointer' : 'default' }}
                >
                  Credits: {credits}
                </Typography>
                {qaDisplay && (
                  <Typography
                    className={`${classes.menuText} ${classes.menuSecondary} ${classes.menuShort} ${classes.serviceText} ${userItem?.staff ? classes.navLink : ''}`}
                    onClick={() => {
                      if (userItem?.staff) {
                        setDefaultCreditType(CreditType.SERVICE_CREDIT);
                        setShowServiceCreditsModal(true);
                      }
                    }}
                    style={{ cursor: userItem?.staff ? 'pointer' : 'default' }}
                  >
                    Service: {serviceCredits}
                  </Typography>
                )}
              </div>
            </div>
          )}

          <HelpButton iconOnly={true} />

          <IconButton
            ref={accountAnchorRef}
            edge="start"
            color="inherit"
            aria-label="account menu"
            aria-controls={accountMenuOpen ? 'account-menu-list' : undefined}
            aria-haspopup="true"
            onClick={handleAccountMenuToggle}
          >
            <AccountCircleIcon
              fontSize='large'
              style={{color: isAuthenticated ? theme.palette.secondary.main : '#ccc'}}
            />
          </IconButton>
          <Popper
            open={accountMenuOpen}
            anchorEl={accountAnchorRef.current}
            transition
            style={{ zIndex: 999 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleAccountMenuClose}>
                    <MenuList autoFocusItem={accountMenuOpen} id="account-menu-list">
                      {isAuthenticated && ([
                        <MenuItem key='userInfo'>
                          {/* <Link href='/account'> */}
                          <Grid container style={{width: 200}}>
                            <Grid item xs={3}>
                              <img src={user.picture} style={{width: 40, height: 40}} />
                            </Grid>
                            <Grid item xs={9}>
                              <Typography noWrap>
                                {(userItem && userItem.name) ? userItem.name : user.nickname}
                              </Typography>
                              {/* When variant='caption', noWrap stops working.  Why? */}
                              <Typography noWrap className={classes.emailText}>
                                {userItem?.email ?? ''}
                              </Typography>
                            </Grid>
                          </Grid>
                          {/* </Link> */}
                        </MenuItem>,
                        <MenuItem key='account-settings' onClick={() => handleMenuItemClick(() => setRedirect('/account'))}>
                          <Grid container>
                            <Grid item xs={12}>
                              <Typography>Account settings</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>,
                        <Divider key='div-account'/>,
                        ...(userItem?.staff && qaDisplay ? [
                          <MenuItem key='admin' onClick={() => handleMenuItemClick(() => setRedirect('/admin'))}>
                            <Grid container>
                              <Grid item xs={12}>
                                <Typography>Admin panel</Typography>
                              </Grid>
                            </Grid>
                          </MenuItem>,
                          <Divider key='div-admin'/>
                        ] : []),
                        <MenuItem key='buy-packages' onClick={() => handleMenuItemClick(() => setRedirect('/buy?p=packages'))}>
                          <Grid container>
                            <Grid item xs={6}>
                              <Typography>Buy scan packages</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>,
                        <MenuItem key='buy-credits' onClick={() => handleMenuItemClick(() => setRedirect('/buy?p=credits'))}>
                          <Grid container>
                            <Grid item xs={6}>
                              <Typography>Buy credit packs</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>,
                        <MenuItem key='buy-frames' onClick={() => handleMenuItemClick(() => setRedirect('/buy?p=frames'))}>
                          <Grid container>
                            <Grid item xs={6}>
                              <Typography>Buy PRO Frames</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>,
                        <Divider key='div'/>,
                        <MenuItem key='signOut' onClick={() => handleMenuItemClick(() => doLogout(logout))}>
                          <Typography>
                            Sign out
                          </Typography>
                        </MenuItem>
                        ]
                      )}
                      {!isAuthenticated && (
                        <MenuItem key='signIn' onClick={() => handleMenuItemClick(loginWithRedirect)}>
                          <Typography>
                            Sign in
                          </Typography>
                        </MenuItem>
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Toolbar>
      </AppBar>
    )}

    <TimedAlert alert={alert}/>

    <ConfirmModal
      visible={!!showWelcomeModal}
      title='Welcome to Bandicoot!'
      message={welcomeMessage}
      onYes={handleCloseWelcome}
      confirmText='OK'
    />

    <NavigateModal
      url={redirect}
      visible={showNavModal}
      setVisible={setShowNavModal}
    />

    <ServiceCreditsModal
      open={showServiceCreditsModal}
      onClose={() => setShowServiceCreditsModal(false)}
      userId={galleryUser}
      userEmail={galleryUserItem?.email}
      defaultCreditType={defaultCreditType}
      currentUserCredits={credits}
      currentServiceCredits={serviceCredits}
    />
  </>);
}
